<template>
  <div class="markup-panel">
    <div class="markup-head">
      <h3 class="markup-title">The block to paste</h3>
      <div class="markup-counts">
        <span v-if="addedCount" class="count-added">+{{ addedCount }}</span>
        <span v-if="removedCount" class="count-removed">−{{ removedCount }}</span>
        <span class="count-unchanged">{{ unchangedCount }} unchanged</span>
        <v-btn
          size="small"
          color="primary"
          variant="flat"
          rounded="pill"
          class="text-none font-weight-bold"
          @click="copyBlock"
        >{{ copied ? 'Copied' : 'Copy' }}</v-btn>
      </div>
    </div>

    <div ref="codePanel" class="markup-code">
      <button
        v-for="line in lines"
        :key="line.n"
        type="button"
        class="code-line"
        :class="[`kind-${line.kind}`, { 'is-active': line.change && line.change === activeChangeId }]"
        :disabled="!line.change"
        @click="selectLine(line)"
      >
        <span class="line-no">{{ line.n }}</span>
        <span class="line-mark">{{ markFor(line.kind) }}</span>
        <span class="line-text">{{ line.text }}</span>
      </button>
    </div>

    <div class="markup-foot">
      <span v-if="hasAttribution">Click any line to see why it is there.</span>
      <span v-else>The spans on this page did not line up, so the block is shown without a diff.</span>
      <span v-if="removedCount" class="foot-note">
        Red lines are in your current markup and are not part of the block Copy gives you.
      </span>
      <span v-if="hasBefore" class="foot-note">
        Your current markup is re-formatted here for comparison, so its spacing will not match your page source.
      </span>
    </div>
  </div>
</template>

<script>
// Leaves a couple of lines of context above the change rather than pinning it to the top edge.
const CONTEXT_LINES_ABOVE = 40

export default {
  name: 'SchemaSolutionMarkup',
  props: {
    lines: {
      type: Array,
      default: () => []
    },
    code: {
      type: String,
      default: ''
    },
    addedCount: {
      type: Number,
      default: 0
    },
    removedCount: {
      type: Number,
      default: 0
    },
    unchangedCount: {
      type: Number,
      default: 0
    },
    hasAttribution: {
      type: Boolean,
      default: true
    },
    hasBefore: {
      type: Boolean,
      default: false
    },
    activeChangeId: {
      type: String,
      default: null
    }
  },
  emits: ['select-change'],
  data() {
    return {
      copied: false
    }
  },
  watch: {
    // Selecting a change should take the reader to that part of the block, not just tint it.
    activeChangeId() {
      this.$nextTick(this.revealActiveChange)
    }
  },
  methods: {
    revealActiveChange() {
      if (!this.activeChangeId) return
      const panel = this.$refs.codePanel
      if (!panel) return
      const first = panel.querySelector('.code-line.is-active')
      if (!first) return
      panel.scrollTop = Math.max(0, first.offsetTop - CONTEXT_LINES_ABOVE)
    },
    markFor(kind) {
      if (kind === 'added') return '+'
      if (kind === 'removed') return '−'
      return ''
    },
    selectLine(line) {
      if (!line.change) return
      this.$emit('select-change', line.change)
    },
    async copyBlock() {
      try {
        await navigator.clipboard.writeText(this.code)
        this.copied = true
        setTimeout(() => { this.copied = false }, 1600)
      } catch (error) {
        console.error('Failed to copy schema block', error)
      }
    }
  }
}
</script>

<style scoped>
.markup-panel {
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
}

.markup-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--brand-sand-300);
}

.markup-title {
  font-family: 'Lora', serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.markup-counts {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  font-weight: 700;
}

.count-added {
  color: #127a6a;
}

.count-removed {
  color: #9b2c2c;
}

.count-unchanged {
  color: #8496bc;
  font-weight: 400;
}

.markup-code {
  position: relative;
  background: var(--brand-ink-950);
  padding: 10px 0;
  max-height: 566px;
  overflow: auto;
}

.code-line {
  display: flex;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.65;
  padding: 1px 14px 1px 0;
  color: #d6e4f5;
  box-shadow: inset 3px 0 0 transparent;
}

.code-line:not(:disabled) {
  cursor: pointer;
}

.line-no {
  flex-shrink: 0;
  width: 42px;
  text-align: right;
  padding-right: 8px;
  color: rgba(214, 228, 245, 0.4);
  user-select: none;
}

.line-mark {
  flex-shrink: 0;
  width: 14px;
  text-align: center;
  user-select: none;
}

.line-text {
  white-space: pre;
  overflow-wrap: anywhere;
}

.kind-added {
  background: rgba(26, 172, 176, 0.1);
  box-shadow: inset 3px 0 0 rgba(26, 172, 176, 0.55);
}

.kind-added .line-mark {
  color: #4fd1c5;
}

.kind-removed {
  background: rgba(180, 35, 24, 0.2);
  color: rgba(214, 228, 245, 0.75);
  box-shadow: inset 3px 0 0 rgba(232, 139, 130, 0.7);
}

.kind-removed .line-mark {
  color: #e88b82;
}

.kind-unchanged,
.kind-plain {
  color: rgba(214, 228, 245, 0.55);
}

.code-line.is-active {
  background: rgba(77, 157, 223, 0.3);
  box-shadow: inset 3px 0 0 var(--brand-ocean-400);
  color: #eaf3ff;
}

.markup-foot {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 9px 16px;
  border-top: 1px solid var(--brand-sand-300);
  font-size: 11.5px;
  color: #8496bc;
}

.foot-note {
  color: #8496bc;
}
</style>
